// Practice-area filtered command center — pipeline + matter list.
// Ported from prototype's view.kind === "practice" branch.

import { Navigate, useNavigate, useParams } from "react-router-dom";
import { OTHER_AREA, practiceAreaById } from "../../data/practice-areas";
import { matterClient, mattersByPracticeArea } from "../../data/matters";
import { PipelineByArea } from "./PipelineByArea";
import { usePageTrail } from "../chrome/trail";
import { EntityLink } from "../chrome/EntityLink";
import { FixtureNotice } from "../chrome/FixtureNotice";

export function PracticeAreaView() {
  const navigate = useNavigate();
  const { area } = useParams<{ area: string }>();
  const pa = area ? practiceAreaById(area) : null;
  usePageTrail([{ label: "Practice" }, { label: pa?.label ?? "Practice area" }]);
  // Other has no page; an old link or bookmark still lands on its list.
  if (area === OTHER_AREA.id) return <Navigate to={OTHER_AREA.to} replace />;
  if (!pa) return <div style={{ padding: 40 }}>Practice area not found.</div>;

  return (
    <main className="cc-main">
      <div className="cc-body">
        <div className="cc-card" style={{ padding: 18 }}>
          <div className="cc-card-head">
            <div
              className="cc-card-title"
              style={{ display: "flex", alignItems: "center", gap: 10 }}
            >
              <span className="pa-dot lg" style={{ background: pa.accent }} />
              {pa.label}
            </div>
            <div className="cc-card-sub">· {pa.tagline}</div>
          </div>
        </div>
        <PipelineByArea
          practiceFilter={pa.id}
          setPracticeFilter={(id) =>
            navigate(id === OTHER_AREA.id ? OTHER_AREA.to : `/practice/${id}`)
          }
        />
        {/* The pipeline above is the server's; this list is still the
            prototype's - say so until the practice-area rebuild (needs the
            13-area taxonomy). */}
        <FixtureNotice>
          The matter list below still runs on prototype data - these matters are not the firm's.
        </FixtureNotice>
        <div className="cc-card">
          <div className="cc-card-head">
            <div className="cc-card-title">Active matters</div>
          </div>
          <div>
            {mattersByPracticeArea(pa.id).map((m) => {
              const c = matterClient(m);
              return (
                <div
                  key={m.eID}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr auto auto",
                    gap: 14,
                    padding: "10px 18px",
                    borderTop: "1px solid var(--line-2)",
                    alignItems: "center",
                  }}
                >
                  <EntityLink
                    kind="matter"
                    id={m.eID}
                    eID={m.eID}
                    subtle
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 11,
                      color: "var(--ink-2)",
                    }}
                  >
                    {m.eID}
                  </EntityLink>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 550 }}>
                      {c ? (
                        <EntityLink kind="client" id={c.id} subtle>
                          {c.name}
                        </EntityLink>
                      ) : (
                        "(unknown)"
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--muted)" }}>
                      {m.subtype || ""} ·{" "}
                      <EntityLink kind="matter" id={m.eID} eID={m.eID} subtle>
                        {m.stage}
                      </EntityLink>
                    </div>
                  </div>
                  <span className="tnum" style={{ fontSize: 10, color: "var(--muted)" }}>
                    opened {m.opened}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      color: m.needsAttention ? "var(--red-600)" : "var(--muted)",
                    }}
                  >
                    {m.needsAttention ? "needs attention" : ""}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </main>
  );
}
